import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import Navbar from './Navbar';
import { SERVER_URL } from './../config';
import { AuthContext } from './../AuthContext';
import '../styles/AccountsPage.css';

const API_URL = "/accounts/";

const AccountsPage = ({ onLogout }) => {
  const { token, isAuthenticated } = useContext(AuthContext);

  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [editableAccount, setEditableAccount] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [newAccount, setNewAccount] = useState({
    name: '',
    bank_name: '',
    account_last4: '',
    card_last4: '',
    active: true,
  });
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    if (isAuthenticated) fetchAccounts();
    // eslint-disable-next-line
  }, [isAuthenticated]);

  const fetchAccounts = async () => {
    if (!token || !isAuthenticated) return;
    try {
      const response = await axios.get(`${SERVER_URL}${API_URL}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const applyFilters = useCallback(() => {
    const filtered = accounts.filter(acc =>
      (showActive && acc.active) || (showInactive && !acc.active)
    );
    setFilteredAccounts(filtered);
  }, [accounts, showActive, showInactive]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (e) => {
    const { name, checked } = e.target;
    if (name === 'showActive') setShowActive(checked);
    if (name === 'showInactive') setShowInactive(checked);
  };

  const openModal = (account) => {
    setSelectedAccount(account);
    setEditableAccount({ ...account });
    setUpdateMessage('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedAccount(null);
    setEditableAccount(null);
    setUpdateMessage('');
    setIsModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    let newValue = value;
    if (type === 'checkbox') newValue = e.target.checked;
    setEditableAccount(prev => ({ ...prev, [name]: newValue }));
  };

  const handleUpdate = async () => {
    if (!token) return;
    try {
      const response = await axios.patch(
        `${SERVER_URL}${API_URL}${selectedAccount.id}/`, // добавлен /
        editableAccount,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setAccounts(prev =>
        prev.map(acc => acc.id === selectedAccount.id ? response.data : acc)
      );
      setUpdateMessage('Account updated successfully.');
    } catch (error) {
      setUpdateMessage('Failed to update account.');
    }
  };

  const handleDelete = async () => {
    if (!token) return;
    try {
      await axios.delete(`${SERVER_URL}${API_URL}${selectedAccount.id}/`, { // добавлен /
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(prev => prev.filter(acc => acc.id !== selectedAccount.id));
      closeModal();
    } catch (error) {
      console.error('Error deleting account:', error);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!token) return;
    try {
      const response = await axios.post(`${SERVER_URL}${API_URL}`, newAccount, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setAccounts([...accounts, response.data]);
      setShowCreateModal(false);
      setNewAccount({
        name: '',
        bank_name: '',
        account_last4: '',
        card_last4: '',
        active: true,
      });
    } catch (error) {
      console.error('Error creating account:', error);
    }
  };

  const handleNewInputChange = (e) => {
    const { name, value, type } = e.target;
    let newValue = value;
    if (type === 'checkbox') newValue = e.target.checked;
    setNewAccount(prev => ({ ...prev, [name]: newValue }));
  };

  return (
    <div className="accounts">
      <Navbar onLogout={onLogout} />
      <div className="content">
        <h1>Accounts</h1>
        <button onClick={() => setShowCreateModal(true)}>Add Account</button>
        {/* Filter Options */}
        <div className="filter-options">
          <label>
            <input
              type="checkbox"
              name="showActive"
              checked={showActive}
              onChange={handleFilterChange}
            />
            Show Active
          </label>
          <label>
            <input
              type="checkbox"
              name="showInactive"
              checked={showInactive}
              onChange={handleFilterChange}
            />
            Show Inactive
          </label>
        </div>
        <div className="accounts-container">
          <table className="accounts-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Bank</th>
                <th>Account Last 4</th>
                <th>Card Last 4</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.map((account, index) => (
                <tr
                  key={account.id}
                  className={index % 2 === 0 ? 'even' : 'odd'}
                  onClick={() => openModal(account)}
                >
                  <td>{account.name}</td>
                  <td>{account.bank_name}</td>
                  <td>{account.account_last4}</td>
                  <td>{account.card_last4}</td>
                  <td>{account.active ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {isModalOpen && editableAccount && (
          <div className="modal">
            <div className="modal-content">
              <button
                type="button"
                className="modal-x"
                onClick={closeModal}
                aria-label="Close"
                title="Close"
              >
              </button>
              <h2>Edit Account</h2>
              <form>
                <label>
                  Name:
                  <input
                    type="text"
                    name="name"
                    value={editableAccount.name}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Bank Name:
                  <input
                    type="text"
                    name="bank_name"
                    value={editableAccount.bank_name}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Account Last 4:
                  <input
                    type="text"
                    name="account_last4"
                    value={editableAccount.account_last4}
                    onChange={handleInputChange}
                    maxLength={4}
                  />
                </label>
                <label>
                  Card Last 4:
                  <input
                    type="text"
                    name="card_last4"
                    value={editableAccount.card_last4}
                    onChange={handleInputChange}
                    maxLength={4}
                  />
                </label>
                <label className="inline-checkbox">
                  Active:
                  <input
                    type="checkbox"
                    name="active"
                    checked={editableAccount.active}
                    onChange={handleInputChange}
                  />
                </label>
              </form>
              {updateMessage && (
                <div className={`modal-message ${updateMessage.includes('success') ? 'success' : 'error'}`}>
                  {updateMessage}
                </div>
              )}
              <div className="modal-buttons">
                <button className="modal-button" onClick={handleUpdate}>
                  Update
                </button>
                <button className="modal-button delete-button" onClick={handleDelete}>
                  Delete
                </button>
                <button className="modal-button close-button" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {showCreateModal && (
          <div className="modal">
            <div className="modal-content">
              <button
                type="button"
                className="modal-x"
                onClick={() => setShowCreateModal(false)}
                aria-label="Close"
                title="Close"
              >
              </button>
              <h2>Create Account</h2>
              <form onSubmit={handleCreateAccount}>
                <label>
                  Name:
                  <input
                    type="text"
                    name="name"
                    value={newAccount.name}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Bank Name:
                  <input
                    type="text"
                    name="bank_name"
                    value={newAccount.bank_name}
                    onChange={handleNewInputChange}
                  />
                </label>
                <label>
                  Account Last 4:
                  <input
                    type="text"
                    name="account_last4"
                    value={newAccount.account_last4}
                    onChange={handleNewInputChange}
                    maxLength={4}
                    required
                  />
                </label>
                <label>
                  Card Last 4:
                  <input
                    type="text"
                    name="card_last4"
                    value={newAccount.card_last4}
                    onChange={handleNewInputChange}
                    maxLength={4}
                  />
                </label>
                <label className="inline-checkbox">
                  Active:
                  <input
                    type="checkbox"
                    name="active"
                    checked={newAccount.active}
                    onChange={handleNewInputChange}
                  />
                </label>
                <button type="submit">Create</button>
              </form>
              <button
                className="modal-button close-button"
                onClick={() => setShowCreateModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountsPage;